/**
 * Layout Theme System Types
 * 
 * Defines all types for the layout theming system that allows
 * administrators to choose from pre-built layout templates.
 */

// ============================================
// NAVIGATION STYLES
// ============================================

export type NavigationStyle = 
  | 'top-bar'           // Classic horizontal navigation bar (default)
  | 'sidebar-left'      // Fixed left sidebar navigation
  | 'sidebar-collapsible' // Hamburger menu that expands to sidebar
  | 'centered-split'    // Centered logo with navigation split left/right
  | 'mega-menu';        // Dropdown navigation with multi-column menus

export type NavigationConfig = {
  style: NavigationStyle;
  sticky: boolean;
  transparent: boolean;
  showLogo: boolean;
  logoPosition: 'left' | 'center' | 'right';
};

// ============================================
// HERO SECTION STYLES
// ============================================

export type HeroStyle = 
  | 'full-image'        // Full-width image with text overlay (default)
  | 'split-content'     // Image on one side, content on other
  | 'video-background'  // Video playing in background
  | 'gradient-only'     // Gradient/color background, no image
  | 'carousel'          // Image slider/carousel
  | 'minimal';          // Simple text-only header

export type HeroConfig = {
  style: HeroStyle;
  height: 'full' | 'large' | 'medium' | 'small';
  overlayOpacity: number; // 0-100
  contentAlignment: 'left' | 'center' | 'right';
  showStats: boolean;
  showCTA: boolean;
};

// ============================================
// CONTENT LAYOUT STYLES
// ============================================

export type ContentLayoutStyle = 
  | 'single-column'     // Full-width single column (default)
  | 'two-column-right'  // Content with right sidebar
  | 'two-column-left'   // Content with left sidebar
  | 'three-column'      // Sidebar - Content - Sidebar
  | 'card-grid'         // Card-based grid layout
  | 'magazine';         // Featured content with grid

export type ContentLayoutConfig = {
  style: ContentLayoutStyle;
  maxWidth: 'full' | 'wide' | 'medium' | 'narrow';
  sidebarWidth: 'narrow' | 'medium' | 'wide';
  showBreadcrumbs: boolean;
};

// ============================================
// FOOTER STYLES
// ============================================

export type FooterStyle = 
  | 'full'              // Multi-column footer with all sections (default)
  | 'simple'            // Single row with minimal info
  | 'centered'          // Centered content, minimal columns
  | 'minimal';          // Just copyright and essential links

export type FooterConfig = {
  style: FooterStyle;
  showNewsletter: boolean;
  showSocialLinks: boolean;
  showQuickLinks: boolean;
  columns: number;
};

// ============================================
// COMPLETE LAYOUT THEME
// ============================================

export type LayoutTheme = {
  id: string;
  name: string;
  description: string;
  thumbnail?: string;
  navigation: NavigationConfig;
  hero: HeroConfig;
  content: ContentLayoutConfig;
  footer: FooterConfig;
};

// ============================================
// LAYOUT THEME PRESET IDS
// ============================================

export type LayoutPresetId = 
  | 'classic'           // Current default layout
  | 'modern-sidebar'    // Left sidebar navigation
  | 'minimal-clean'     // Minimal, clean design
  | 'corporate'         // Professional corporate look
  | 'magazine'          // Magazine/blog style
  | 'landing-page'      // Marketing landing page style
  | 'dashboard'        // App-like dashboard layout
  | 'portfolio'         // Creative portfolio layout
  | 'saas-product'      // Modern SaaS/product page style
  | 'ecommerce'         // E-commerce/shop layout
  | 'nonprofit'         // Nonprofit/mission-driven layout
  | 'education'         // Education/learning platform style
  | 'healthcare'        // Healthcare/medical trust-focused
  | 'restaurant'        // Restaurant/food service layout
  | 'agency'            // Creative agency/studio layout
  | 'community'        // Community/social engagement layout
  | 'blog-focus';       // Content-first blog layout

// ============================================
// LAYOUT SETTINGS (stored in database)
// ============================================

export type LayoutSettings = {
  preset: LayoutPresetId;
  customizations?: Partial<LayoutTheme>;
};

// ============================================
// LAYOUT CONTEXT TYPE
// ============================================

export type LayoutContextType = {
  currentLayout: LayoutTheme;
  presetId: LayoutPresetId;
  isLoading: boolean;
  setLayout: (presetId: LayoutPresetId) => Promise<void>;
};

// ============================================
// DEFAULT VALUES
// ============================================

export const DEFAULT_NAVIGATION_CONFIG: NavigationConfig = {
  style: 'top-bar',
  sticky: true,
  transparent: false,
  showLogo: true,
  logoPosition: 'left',
};

export const DEFAULT_HERO_CONFIG: HeroConfig = {
  style: 'full-image',
  height: 'large',
  overlayOpacity: 60,
  contentAlignment: 'left',
  showStats: true,
  showCTA: true,
};

export const DEFAULT_CONTENT_CONFIG: ContentLayoutConfig = {
  style: 'single-column',
  maxWidth: 'wide',
  sidebarWidth: 'medium',
  showBreadcrumbs: false,
};

export const DEFAULT_FOOTER_CONFIG: FooterConfig = {
  style: 'full',
  showNewsletter: true,
  showSocialLinks: true,
  showQuickLinks: true,
  columns: 4,
};




